import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@testbusters/button';
import { Icon } from '@testbusters/icon';
import PageGray from '../components/General/PageGray';
import ExerciseStep3Subjects from '../components/CustomExercise/ExerciseStep3Subjects';
import ExerciseStep4Questions from '../components/CustomExercise/ExerciseStep4Questions';
import {
  useGetWrongSubjects,
  useCreateTrainingAttemptFromWrong,
} from '../hooks/favouriteQuestions/useFavouriteQuestions';
import type { ApiSubject } from '../api/pools.api';
import H2 from '../components/Typography/Headings/H2';
import P from '../components/Typography/P';
import Span from '../components/Typography/Span';
import TransparentButton from '../components/General/TransparentButton';
import { cn } from '../lib/utills';

export const Route = createFileRoute('/mie-domande/crea-sbagliate')({
  component: RouteComponent,
});

type Step = 'subjects' | 'questions';
const STEPS: Step[] = ['subjects', 'questions'];

function StepIndicator({ currentStep }: { currentStep: Step }) {
  const { t } = useTranslation();
  const currentIndex = STEPS.indexOf(currentStep);

  const labels: Record<Step, string> = {
    subjects: t('exercise.steps.subjects'),
    questions: t('exercise.steps.questionsTitle'),
  };

  return (
    <>
      {/* Mobile */}
      <div className="sm:hidden flex flex-col items-center gap-3">
        <div className="flex items-center gap-0">
          {STEPS.map((step, index) => {
            const isActive = index === currentIndex;
            const isCompleted = index < currentIndex;
            const isLast = index === STEPS.length - 1;
            return (
              <div key={step} className="flex items-center">
                <div className="flex items-center justify-center h-7 w-7">
                  {isCompleted ? (
                    <Icon name="circle-success-filled" className="text-[22px] text-background-accent" />
                  ) : (
                    <div className={cn(
                      'size-4 ring-offset-1! ring-1! ring-button-primary-primary-default rounded-full transition-colors',
                      isActive ? 'bg-button-primary-primary-default' : 'bg-white border-gray-300',
                    )} />
                  )}
                </div>
                {!isLast && (
                  <div className={cn('h-0.5 w-8 transition-colors', isCompleted ? 'bg-background-accent' : 'bg-gray-200')} />
                )}
              </div>
            );
          })}
        </div>
        <div className="flex flex-col items-center gap-0.5">
          <Span className="text-sm font-semibold text-center">{labels[currentStep]}</Span>
          <Span className="text-xs text-text-body-description">{currentIndex + 1} / {STEPS.length}</Span>
        </div>
      </div>

      {/* Desktop */}
      <div className="hidden sm:flex items-start justify-center gap-0">
        {STEPS.map((step, index) => {
          const isActive = index === currentIndex;
          const isCompleted = index < currentIndex;
          const isLast = index === STEPS.length - 1;
          return (
            <div key={step} className="flex items-start">
              <div className="flex flex-col items-center gap-4 w-24">
                {isCompleted ? (
                  <div className="h-[28px] flex items-center justify-center">
                    <Icon name="circle-success-filled" className="text-[28px] text-background-accent" />
                  </div>
                ) : (
                  <div className="h-[28px] flex items-center justify-center">
                    <div className={cn(
                      'size-5 ring-offset-1! ring-1! ring-button-primary-primary-default rounded-full transition-colors',
                      isActive ? 'bg-button-primary-primary-default border-button-primary-primary-default' : 'bg-white border-gray-300',
                    )} />
                  </div>
                )}
                <Span className="text-center leading-tight! whitespace-nowrap font-semibold">{labels[step]}</Span>
              </div>
              {!isLast && (
                <div className={cn('h-0.5 w-37.5 mt-3.25 -mx-9.5 transition-colors', isCompleted ? 'bg-background-accent' : 'bg-gray-200')} />
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

function Spinner() {
  return (
    <div className="flex justify-center py-8">
      <div className="w-6 h-6 border-2 border-background-accent border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState<Step>('subjects');
  const [selectedTopicIds, setSelectedTopicIds] = useState<Set<string>>(new Set());
  const [questionCount, setQuestionCount] = useState(20);

  const { subjects, isFetching } = useGetWrongSubjects();
  const { mutate: createAttempt, isPending: isCreating } = useCreateTrainingAttemptFromWrong();

  const currentStepIndex = STEPS.indexOf(currentStep);

  const maxQuestions = subjects.reduce(
    (total, subject) =>
      total +
      subject.topics
        .filter((t) => selectedTopicIds.has(t.topicId))
        .reduce((sum, t) => sum + t.count, 0),
    0,
  );

  const STEP_TITLES: Record<Step, string> = {
    subjects: t('exercise.steps.subjectsTitle'),
    questions: t('exercise.steps.questionsTitle'),
  };

  const STEP_SUBTITLES: Record<Step, string> = {
    subjects: t('exercise.steps.subjectsSubtitle'),
    questions: '',
  };

  const handleClose = () => navigate({ to: '/mie-domande' });

  const handleBack = () => {
    if (currentStepIndex > 0) setCurrentStep(STEPS[currentStepIndex - 1]);
    else navigate({ to: '/mie-domande' });
  };

  const handleConfirm = () => {
    if (currentStep === 'questions') {
      const subjectIds = subjects
        .filter((s) => s.topics.some((t) => selectedTopicIds.has(t.topicId)))
        .map((s) => s._id);

      createAttempt(
        {
          subjectIds,
          topicIds: [...selectedTopicIds],
          answersCount: questionCount,
        },
        {
          onSuccess: (attempt) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            navigate({ to: '/esercitazioni/$id', params: { id: attempt._id }, search: { from: 'mie-domande' } as any });
          },
        },
      );
      return;
    }

    if (currentStep === 'subjects') {
      setQuestionCount(Math.min(20, maxQuestions));
      setCurrentStep('questions');
    }
  };

  const handleTopicToggle = (topicId: string) => {
    setSelectedTopicIds((prev) => {
      const next = new Set(prev);
      next.has(topicId) ? next.delete(topicId) : next.add(topicId);
      return next;
    });
  };

  const handleSubjectToggle = (subject: ApiSubject) => {
    const allTopicIds = subject.topics.map((t) => t.topicId);
    const allSelected = allTopicIds.every((id) => selectedTopicIds.has(id));
    setSelectedTopicIds((prev) => {
      const next = new Set(prev);
      if (allSelected) allTopicIds.forEach((id) => next.delete(id));
      else allTopicIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const isConfirmDisabled =
    isCreating || (currentStep === 'subjects' && selectedTopicIds.size === 0);

  return (
    <PageGray className="min-h-screen flex flex-col justify-between px-4! py-6! lg:p-11! gap-6">
      <div className="flex justify-center relative mb-24 lg:mb-12">
        <TransparentButton
          onClick={handleClose}
          className="absolute top-0 right-0 lg:right-6 flex items-center gap-1 text-sm transition-colors"
        >
          <span className="first-letter:uppercase">{t('common.close')}</span>{' '}
          <Icon name="close" className="text-2xl" />
        </TransparentButton>

        <div className="w-full flex flex-col gap-9 relative max-w-225">
          <StepIndicator currentStep={currentStep} />

          <div className="flex flex-col gap-2 text-center">
            <H2 className="text-3xl! font-bold text-button-primary-primary-default">
              {STEP_TITLES[currentStep]}
            </H2>
            {STEP_SUBTITLES[currentStep] && (
              <P className="text-sm text-gray-500">{STEP_SUBTITLES[currentStep]}</P>
            )}
          </div>

          <div>
            {currentStep === 'subjects' && (
              isFetching ? (
                <Spinner />
              ) : (
                <ExerciseStep3Subjects
                  subjects={subjects}
                  selectedTopicIds={selectedTopicIds}
                  onTopicToggle={handleTopicToggle}
                  onSubjectToggle={handleSubjectToggle}
                />
              )
            )}
            {currentStep === 'questions' && (
              <ExerciseStep4Questions
                count={questionCount}
                maxCount={maxQuestions}
                onChange={setQuestionCount}
              />
            )}
          </div>
        </div>
      </div>

      <div className="bg-white fixed bottom-0 left-0 right-0 flex justify-between items-center gap-3 px-4 py-4 lg:px-9 lg:py-7 shadow-2xl">
        <Button variant="tertiary" className="flex-1 lg:flex-none lg:min-w-52" onClick={handleBack}>
          {currentStepIndex === 0 ? (t('common.cancel') ?? 'Annulla') : (t('common.back') ?? 'Indietro')}
        </Button>
        <Button
          variant="primary"
          className="flex-1 lg:flex-none lg:min-w-52"
          disabled={isConfirmDisabled}
          onClick={handleConfirm}
        >
          {t('common.confirm') ?? 'Conferma'}
        </Button>
      </div>
    </PageGray>
  );
}
